import { useState } from 'react';
import { useNavigate } from 'react-router';
import {
  Typography,
  Box,
  CircularProgress,
  Button,
  Paper,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
} from '@mui/material';
import { ArrowLeft } from 'lucide-react';
import { AppLayout } from '@/components/layout/AppLayout';
import { useAuth } from '@/hooks/useAuth';
import { useCreateCompany } from '@/lib/query/hooks';
import type { CreateCompanyRequest } from '@/lib/types/mise/company';

export function CreateCompanyPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [companyId, setCompanyId] = useState('');
  const [validationError, setValidationError] = useState('');
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const createCompanyMutation = useCreateCompany();

  // バリデーション: 小文字英数字、ハイフン、アンダースコアのみ
  const validateCompanyId = (id: string): boolean => {
    if (!id.trim()) {
      setValidationError('企業IDは必須です');
      return false;
    }
    const regex = /^[a-z0-9_-]+$/;
    if (!regex.test(id)) {
      setValidationError('企業IDは小文字英数字、ハイフン、アンダースコアのみ使用できます');
      return false;
    }
    setValidationError('');
    return true;
  };

  // 入力変更ハンドラー
  const handleCompanyIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCompanyId(value);
    if (value) {
      validateCompanyId(value);
    } else {
      setValidationError('');
    }
  };

  // 登録ボタンクリック → 確認ダイアログ表示
  const handleRegisterClick = () => {
    if (validateCompanyId(companyId)) {
      setOpenConfirmDialog(true);
    }
  };

  // 確認ダイアログで「登録する」をクリック
  const handleConfirmRegister = async () => {
    setOpenConfirmDialog(false);

    const request: CreateCompanyRequest = {
      company_id: companyId,
    };

    try {
      await createCompanyMutation.mutateAsync(request);
      setSnackbar({
        open: true,
        message: '企業を登録しました',
        severity: 'success',
      });
      // 企業詳細ページに遷移
      setTimeout(() => {
        navigate(`/mise/companies/${companyId}`);
      }, 1000);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : '企業の登録に失敗しました';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    }
  };

  // 確認ダイアログをキャンセル
  const handleCancelDialog = () => {
    setOpenConfirmDialog(false);
  };

  // Snackbarを閉じる
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // 戻るボタン
  const handleBack = () => {
    navigate('/mise/companies');
  };

  // User Pool名とApp Client名を生成（表示用）
  // mise 用と StoreHub 用で別々の App Client が同じ User Pool 内に作られる
  const userPoolName = companyId ? `mise-${companyId}-user-pool` : '';
  const miseAppClientName = companyId ? `mise-${companyId}-app-client` : '';
  const hubAppClientName = companyId ? `mise-${companyId}-hub-app-client` : '';

  // 認証ローディング中
  if (authLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress />
        <Typography>認証状態を確認中...</Typography>
      </Box>
    );
  }

  // 未認証の場合
  if (!isAuthenticated) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress />
        <Typography>サインインページにリダイレクト中...</Typography>
      </Box>
    );
  }

  return (
    <>
      <AppLayout showSearch={false}>
        <Box sx={{ width: '90%', maxWidth: '800px', mx: 'auto', px: 2 }}>
          {/* ヘッダー */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, mt: 2 }}>
            <Button startIcon={<ArrowLeft />} onClick={handleBack} sx={{ mr: 2 }}>
              戻る
            </Button>
            <Typography variant="h5" fontWeight="bold">
              企業新規登録
            </Typography>
          </Box>

          {/* 登録フォーム */}
          <Paper sx={{ p: 4 }}>
            <Typography variant="h6" gutterBottom>
              企業情報
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              企業IDを入力してください。User PoolとApp Clientが自動的に作成されます。
            </Typography>

            <Box sx={{ mt: 3 }}>
              <TextField
                label="企業ID"
                value={companyId}
                onChange={handleCompanyIdChange}
                error={!!validationError}
                helperText={
                  validationError || '小文字英数字、ハイフン、アンダースコアのみ使用できます'
                }
                fullWidth
                required
                placeholder="例: test-company"
              />
            </Box>

            {/* 作成されるリソースのプレビュー */}
            {companyId && !validationError && (
              <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  作成されるリソース:
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  • User Pool: <strong>{userPoolName}</strong>
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  • App Client (mise): <strong>{miseAppClientName}</strong>
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  • App Client (StoreHub): <strong>{hubAppClientName}</strong>
                </Typography>
              </Box>
            )}

            {/* 登録ボタン */}
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleRegisterClick}
                disabled={!companyId || !!validationError || createCompanyMutation.isPending}
              >
                {createCompanyMutation.isPending ? '登録中...' : '登録'}
              </Button>
            </Box>
          </Paper>
        </Box>
      </AppLayout>

      {/* 確認ダイアログ */}
      <Dialog open={openConfirmDialog} onClose={handleCancelDialog}>
        <DialogTitle>企業登録の確認</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>以下の内容で企業を登録します。よろしいですか？</Typography>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="body2">
              <strong>企業ID:</strong> {companyId}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>User Pool:</strong> {userPoolName}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>App Client (mise):</strong> {miseAppClientName}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>App Client (StoreHub):</strong> {hubAppClientName}
            </Typography>
          </Box>
          <Alert severity="info" sx={{ mt: 2 }}>
            Cognito User Pool と mise / StoreHub の App Client 2 つが自動作成されます。
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDialog}>キャンセル</Button>
          <Button onClick={handleConfirmRegister} variant="contained" color="primary">
            登録する
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar通知 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
